export const WEBVIEWER_OVERSCROLL_EDGE_PX = 2;
/** Finger movement beyond this is a document pan, not a tap-to-place on iPad. */
export const WEBVIEWER_TOUCH_PAN_THRESHOLD_PX = 10;

export function getPageOverscrollDelta({
	deltaY,
	deltaX,
	scrollTop,
	scrollHeight,
	clientHeight,
	currentPage,
	pageCount,
	edgePx = WEBVIEWER_OVERSCROLL_EDGE_PX,
}: {
	deltaY: number;
	deltaX: number;
	scrollTop: number;
	scrollHeight: number;
	clientHeight: number;
	currentPage?: number;
	pageCount?: number;
	edgePx?: number;
}): number {
	if (Math.abs(deltaY) <= Math.abs(deltaX)) return 0;

	const hasInnerScroll = scrollHeight > clientHeight + edgePx;
	if (hasInnerScroll) {
		const atTop = scrollTop <= edgePx;
		const atBottom = scrollTop + clientHeight >= scrollHeight - edgePx;
		if (deltaY > 0 && atBottom) return deltaY;
		if (deltaY < 0 && atTop) return deltaY;
		return 0;
	}

	if (typeof currentPage === 'number' && typeof pageCount === 'number' && pageCount > 0) {
		if (deltaY > 0 && currentPage >= pageCount) return deltaY;
		if (deltaY < 0 && currentPage <= 1) return deltaY;
	}

	return 0;
}

export function isWebViewerPanToolActive(inst: {
	Core?: {
		documentViewer?: {
			getToolMode?: () => { name?: string } | string | null;
			getTool?: (name: string) => unknown;
		};
	};
	UI?: { getToolMode?: () => string };
}): boolean {
	const documentViewer = inst?.Core?.documentViewer;
	const currentTool = documentViewer?.getToolMode?.();
	const panTool = documentViewer?.getTool?.('Pan');
	if (currentTool && panTool && currentTool === panTool) return true;

	const currentName =
		typeof currentTool === 'string' ? currentTool : currentTool?.name;
	if (currentName === 'Pan' || currentName === 'PanTool') return true;

	const uiMode = inst?.UI?.getToolMode?.();
	return uiMode === 'Pan' || uiMode === 'PanTool';
}

/**
 * Signature / Insert / Fill & Sign tools capture one-finger drags on iPad, so the
 * PDF cannot scroll until Hand (Pan) is selected. Treat a moved finger as a pan.
 */
export function shouldTreatTouchAsDocumentPan({
	isPanToolActive,
	moveDistance,
	thresholdPx = WEBVIEWER_TOUCH_PAN_THRESHOLD_PX,
}: {
	isPanToolActive: boolean;
	moveDistance: number;
	thresholdPx?: number;
}): boolean {
	if (isPanToolActive) return false;
	return moveDistance >= thresholdPx;
}

export function getLockedToolScrollDelta({
	deltaY,
	deltaX,
}: {
	deltaY: number;
	deltaX: number;
}): number {
	if (Math.abs(deltaY) <= Math.abs(deltaX)) return 0;
	return deltaY;
}

/**
 * When a finger-pan hits the top/bottom of the PDF scroller, continue that
 * gesture on the page so iPad users can reach controls below the editor.
 * When Signature/Stamp tools are active, one-finger drags still scroll the PDF.
 */
export function attachWebViewerOverscrollToPage(inst: any): () => void {
	const documentViewer = inst?.Core?.documentViewer;
	if (!documentViewer?.getScrollViewElement) return () => undefined;

	let lastY = 0;
	let lastX = 0;
	let startY = 0;
	let startX = 0;
	let gestureIsDocumentPan = false;
	let scrollView: HTMLElement | null = null;
	let attachedTargets: HTMLElement[] = [];

	const onTouchStart = (event: TouchEvent) => {
		if (event.touches.length !== 1) return;
		lastY = event.touches[0].clientY;
		lastX = event.touches[0].clientX;
		startY = lastY;
		startX = lastX;
		gestureIsDocumentPan = false;
	};

	const onTouchMove = (event: TouchEvent) => {
		if (event.touches.length !== 1) return;

		const y = event.touches[0].clientY;
		const x = event.touches[0].clientX;
		const deltaY = lastY - y;
		const deltaX = lastX - x;
		lastY = y;
		lastX = x;

		const panToolActive = isWebViewerPanToolActive(inst);
		if (!panToolActive) {
			const moveDistance = Math.hypot(x - startX, y - startY);
			if (
				shouldTreatTouchAsDocumentPan({
					isPanToolActive: panToolActive,
					moveDistance,
				})
			) {
				gestureIsDocumentPan = true;
			}
			if (!gestureIsDocumentPan) return;

			const innerDelta = getLockedToolScrollDelta({ deltaY, deltaX });
			if (innerDelta !== 0 && scrollView) {
				const previousTop = scrollView.scrollTop;
				scrollView.scrollTop += innerDelta;
				if (event.cancelable) event.preventDefault();
				if (scrollView.scrollTop !== previousTop) return;
			}
		}

		const pageDelta = getPageOverscrollDelta({
			deltaY,
			deltaX,
			scrollTop: scrollView?.scrollTop ?? 0,
			scrollHeight: scrollView?.scrollHeight ?? 0,
			clientHeight: scrollView?.clientHeight ?? 0,
			currentPage: documentViewer.getCurrentPage?.(),
			pageCount: documentViewer.getPageCount?.(),
		});
		if (pageDelta === 0) return;

		window.scrollBy(0, pageDelta);
	};

	const onTouchEnd = (event: TouchEvent) => {
		if (!gestureIsDocumentPan) return;
		if (event.cancelable) event.preventDefault();
		gestureIsDocumentPan = false;
	};

	const unbind = () => {
		attachedTargets.forEach((target) => {
			target.removeEventListener('touchstart', onTouchStart, true);
			target.removeEventListener('touchmove', onTouchMove, true);
			target.removeEventListener('touchend', onTouchEnd, true);
			target.removeEventListener('touchcancel', onTouchEnd, true);
		});
		attachedTargets = [];
		scrollView = null;
	};

	const bind = () => {
		unbind();
		scrollView = (documentViewer.getScrollViewElement?.() as HTMLElement | null) ?? null;
		const viewerElement = (documentViewer.getViewerElement?.() as HTMLElement | null) ?? null;
		const targets = [scrollView, viewerElement].filter(
			(element, index, list): element is HTMLElement =>
				!!element && list.indexOf(element) === index,
		);
		targets.forEach((target) => {
			target.addEventListener('touchstart', onTouchStart, { capture: true, passive: true });
			target.addEventListener('touchmove', onTouchMove, { capture: true, passive: false });
			target.addEventListener('touchend', onTouchEnd, { capture: true, passive: false });
			target.addEventListener('touchcancel', onTouchEnd, { capture: true, passive: false });
		});
		attachedTargets = targets;
		if (scrollView) {
			scrollView.style.overscrollBehaviorY = 'auto';
		}
	};

	documentViewer.addEventListener('documentLoaded', bind);
	bind();

	return () => {
		documentViewer.removeEventListener('documentLoaded', bind);
		unbind();
	};
}
